import React, { useState, useCallback } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { exportAnnotatedPDF, downloadPDF } from '../../utils/exportAnnotatedPDF';

/**
 * PDFExportModal - Modal for exporting annotated PDF
 */
export const PDFExportModal = ({
  onClose,
  pdfLayer,
  annotations = [],
  pdfMeasurements = [],
  units = 'imperial',
  isMobile = false,
}) => {
  const [exportScope, setExportScope] = useState('current'); // 'current' or 'all'
  const [includeAnnotations, setIncludeAnnotations] = useState(true);
  const [includeMeasurements, setIncludeMeasurements] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  const hasMultiplePages = pdfLayer?.pageCount > 1;

  const handleExport = useCallback(async () => {
    if (!pdfLayer?.pdfDoc) {
      setError('No PDF loaded. Please import a PDF first.');
      return;
    }

    setIsExporting(true);
    setError(null);
    setProgress(10);

    try {
      // Get the original PDF bytes from the pdfDoc
      // The pdfDoc is a pdfjs document, we need to get the data from it
      const pdfData = pdfLayer.pdfArrayBuffer;

      if (!pdfData) {
        setError('Cannot access original PDF data. Please re-import the PDF.');
        setIsExporting(false);
        return;
      }

      setProgress(30);

      // Filter annotations and measurements if needed
      const annotationsToExport = includeAnnotations ? annotations : [];
      const measurementsToExport = includeMeasurements ? pdfMeasurements : [];

      setProgress(50);

      // Export the PDF
      const pdfBytes = await exportAnnotatedPDF({
        originalPdfBytes: pdfData,
        pdfLayer,
        annotations: annotationsToExport,
        pdfMeasurements: measurementsToExport,
        units,
        pageNumber: exportScope === 'all' ? 'all' : (pdfLayer.currentPage || 1),
      });

      setProgress(90);

      // Generate filename
      const baseFilename = pdfLayer.fileName?.replace(/\.pdf$/i, '') || 'floor-plan';
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${baseFilename}_annotated_${timestamp}.pdf`;

      // Download the PDF
      downloadPDF(pdfBytes, filename);

      setProgress(100);

      // Close modal after short delay
      setTimeout(() => {
        onClose?.();
      }, 500);

    } catch (err) {
      console.error('PDF export error:', err);
      setError(`Export failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  }, [pdfLayer, annotations, pdfMeasurements, units, exportScope, includeAnnotations, includeMeasurements, onClose]);

  const annotationCounts = {
    stamps: annotations.filter(a => a.type === 'stamp').length,
    clouds: annotations.filter(a => a.type === 'cloud').length,
    arrows: annotations.filter(a => a.type === 'arrow').length,
    rectangles: annotations.filter(a => a.type === 'rectangle').length,
    circles: annotations.filter(a => a.type === 'circle').length,
    freehand: annotations.filter(a => a.type === 'freehand').length,
    callouts: annotations.filter(a => a.type === 'callout').length,
  };

  const measurementCounts = {
    linear: pdfMeasurements.filter(m => m.type === 'linear').length,
    area: pdfMeasurements.filter(m => m.type === 'area').length,
  };

  const totalAnnotations = annotations.length;
  const totalMeasurements = pdfMeasurements.length;

  return (
    <FloatingPanel
      title="Export Annotated PDF"
      onClose={onClose}
      x={isMobile ? 10 : 100}
      y={isMobile ? 60 : 100}
      width={isMobile ? 300 : 360}
      isMobile={isMobile}
    >
      <div style={{ padding: '16px' }}>
        {error && (
          <div style={{
            background: 'rgba(255,80,80,0.2)',
            border: '1px solid rgba(255,80,80,0.5)',
            borderRadius: '6px',
            padding: '10px',
            marginBottom: '16px',
            color: '#ff6666',
            fontSize: '12px',
          }}>
            {error}
          </div>
        )}

        {!pdfLayer && (
          <div style={{
            background: 'rgba(255,170,0,0.15)',
            border: '1px solid rgba(255,170,0,0.4)',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px',
            color: '#ffaa00',
            fontSize: '12px',
          }}>
            No PDF is currently loaded. Import a PDF first to export with annotations.
          </div>
        )}

        {pdfLayer && (
          <>
            <div style={{
              background: 'rgba(0,200,255,0.1)',
              borderRadius: '6px',
              padding: '10px',
              marginBottom: '16px',
            }}>
              <div style={{ color: '#00c8ff', fontSize: '12px', fontWeight: '500' }}>
                {pdfLayer.fileName || 'Imported PDF'}
              </div>
              {hasMultiplePages && (
                <div style={{ color: '#6080a0', fontSize: '11px', marginTop: '4px' }}>
                  {pdfLayer.pageCount} pages | Currently on page {pdfLayer.currentPage || 1}
                </div>
              )}
            </div>

            {/* Export Scope */}
            {hasMultiplePages && (
              <PanelSection title="Export Pages">
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <button
                    onClick={() => setExportScope('current')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: exportScope === 'current' ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
                      border: `2px solid ${exportScope === 'current' ? '#00c8ff' : '#3a4a5a'}`,
                      borderRadius: '6px',
                      color: exportScope === 'current' ? '#00c8ff' : '#6080a0',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    Current Page
                    <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.7 }}>
                      Page {pdfLayer.currentPage || 1} only
                    </div>
                  </button>
                  <button
                    onClick={() => setExportScope('all')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: exportScope === 'all' ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
                      border: `2px solid ${exportScope === 'all' ? '#00c8ff' : '#3a4a5a'}`,
                      borderRadius: '6px',
                      color: exportScope === 'all' ? '#00c8ff' : '#6080a0',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    All Pages
                    <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.7 }}>
                      Export all {pdfLayer.pageCount} pages
                    </div>
                  </button>
                </div>
              </PanelSection>
            )}

            {/* Include Options */}
            <PanelSection title="Include in Export">
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '6px',
                cursor: 'pointer',
                marginBottom: '8px',
              }}>
                <input
                  type="checkbox"
                  checked={includeAnnotations}
                  onChange={(e) => setIncludeAnnotations(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#e0e8f0', fontSize: '12px' }}>Annotations</div>
                  <div style={{ color: '#6080a0', fontSize: '10px', marginTop: '2px' }}>
                    {totalAnnotations === 0 ? 'None' : (
                      [
                        annotationCounts.stamps > 0 && `${annotationCounts.stamps} stamp${annotationCounts.stamps > 1 ? 's' : ''}`,
                        annotationCounts.clouds > 0 && `${annotationCounts.clouds} cloud${annotationCounts.clouds > 1 ? 's' : ''}`,
                        annotationCounts.arrows > 0 && `${annotationCounts.arrows} arrow${annotationCounts.arrows > 1 ? 's' : ''}`,
                        annotationCounts.rectangles > 0 && `${annotationCounts.rectangles} rect`,
                        annotationCounts.circles > 0 && `${annotationCounts.circles} circle${annotationCounts.circles > 1 ? 's' : ''}`,
                        annotationCounts.freehand > 0 && `${annotationCounts.freehand} freehand`,
                        annotationCounts.callouts > 0 && `${annotationCounts.callouts} callout${annotationCounts.callouts > 1 ? 's' : ''}`,
                      ].filter(Boolean).join(', ') || 'None'
                    )}
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '6px',
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={includeMeasurements}
                  onChange={(e) => setIncludeMeasurements(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#e0e8f0', fontSize: '12px' }}>PDF Measurements</div>
                  <div style={{ color: '#6080a0', fontSize: '10px', marginTop: '2px' }}>
                    {totalMeasurements === 0 ? 'None' : (
                      [
                        measurementCounts.linear > 0 && `${measurementCounts.linear} linear`,
                        measurementCounts.area > 0 && `${measurementCounts.area} area`,
                      ].filter(Boolean).join(', ')
                    )}
                  </div>
                </div>
              </label>
            </PanelSection>

            {/* Export Progress */}
            {isExporting && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${progress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #00c8ff, #00ffaa)',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                <div style={{
                  textAlign: 'center',
                  color: '#6080a0',
                  fontSize: '11px',
                  marginTop: '8px',
                }}>
                  {progress < 30 ? 'Preparing PDF...' :
                   progress < 50 ? 'Processing annotations...' :
                   progress < 90 ? 'Generating PDF...' :
                   'Downloading...'}
                </div>
              </div>
            )}

            {/* Export Button */}
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={isExporting || (!includeAnnotations && !includeMeasurements)}
              style={{ width: '100%', marginTop: '8px' }}
            >
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </Button>

            {(!includeAnnotations && !includeMeasurements) && (
              <div style={{
                textAlign: 'center',
                color: '#ffaa00',
                fontSize: '10px',
                marginTop: '8px',
              }}>
                Select at least one option to export
              </div>
            )}

            <div style={{
              marginTop: '16px',
              padding: '10px',
              background: 'rgba(0,255,170,0.08)',
              borderRadius: '6px',
              fontSize: '10px',
              color: '#6080a0',
            }}>
              <strong style={{ color: '#00ffaa' }}>Note:</strong> The exported PDF will contain
              all markup flattened onto the original PDF. Original PDF quality is preserved.
            </div>
          </>
        )}
      </div>
    </FloatingPanel>
  );
};

export default PDFExportModal;
